import store from '@/store';

/**
 * Utility for formatting and parsing money amounts.
 *
 * By default (Apex requirements):
 * - Thousands separator: space
 * - Decimal separator: comma
 * - No trailing zeros if value is an integer
 *
 * A provider whose base currency is MXN gets "42,436.83" instead: that is how every carrier
 * document a Mexican provider works from writes money, and "42 436,83" beside it was being read as
 * a different number.
 */

const DEFAULT_MONEY_FORMAT = { group: ' ', decimal: ',', locale: 'ru-RU' };
const MONEY_FORMAT_BY_CURRENCY = {
  MXN: { group: ',', decimal: '.', locale: 'en-US' },
};

/**
 * Separators for the selected insurance provider's base currency. Read at call time, so a
 * provider switch takes effect on the next render.
 * @returns {{group: string, decimal: string, locale: string}}
 */
export const getMoneySeparators = () => {
  const currencyCode = store.getters?.selectedInsuranceProviderDetails?.currencyCode;
  return MONEY_FORMAT_BY_CURRENCY[currencyCode] || DEFAULT_MONEY_FORMAT;
};

/**
 * Locale for the screens that format money in the browser's locale rather than with formatAmount:
 * the provider currency's own locale when it has one (MXN), otherwise undefined, i.e. the browser's.
 * @returns {string|undefined}
 */
export const getBrowserMoneyLocale = () => {
  const currencyCode = store.getters?.selectedInsuranceProviderDetails?.currencyCode;
  return MONEY_FORMAT_BY_CURRENCY[currencyCode]?.locale;
};

/**
 * Parses a formatted string back into a float number.
 * @param {string} value - The formatted string (e.g., "1 000,50", or "1,000.50" for MXN).
 * @returns {number|null} - The parsed number or null if invalid.
 */
export const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const { decimal } = getMoneySeparators();
  let cleanValue = String(value).replace(/\s/g, '');
  cleanValue = decimal === '.' ? cleanValue.replace(/,/g, '') : cleanValue.replace(',', '.');
  const numberValue = parseFloat(cleanValue);
  return Number.isNaN(numberValue) ? null : numberValue;
};

/**
 * Formats a number or string to the provider's money format.
 * @param {number|string} value - The value to format.
 * @returns {string} - The formatted string.
 */
export const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const numberValue = typeof value === 'string' ? parseAmount(value) : value;
  if (numberValue === null || Number.isNaN(numberValue)) return '';

  return new Intl.NumberFormat(getMoneySeparators().locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(numberValue).replace(/\u00A0/g, ' ');
};

/**
 * Compact amount for dense goal displays: 1280000 -> "1.28M", 310000 -> "310k", 950 -> "950".
 * Falls back to formatAmount below 1000. Max 2 decimals, trailing zeros stripped.
 * @param {number|string} value - The value to format.
 * @returns {string} - The abbreviated string.
 */
export const formatAmountShort = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const numberValue = typeof value === 'string' ? parseAmount(value) : value;
  if (numberValue === null || Number.isNaN(numberValue)) return '';
  const abs = Math.abs(numberValue);
  const trim = (n) => String(parseFloat(n.toFixed(2)));
  if (abs >= 1e6) return `${trim(numberValue / 1e6)}M`;
  if (abs >= 1e3) return `${trim(numberValue / 1e3)}k`;
  return formatAmount(numberValue);
};

/**
 * Whether the text typed so far holds a decimal marker. With the default format either "," or "."
 * starts the decimals; with MXN a "," is a thousands separator, so only "." does.
 * @param {string} text - Raw text as typed.
 * @returns {boolean}
 */
export const hasDecimalMarker = (text) => {
  const { decimal } = getMoneySeparators();
  return decimal === '.' ? text.includes('.') : text.includes(',') || text.includes('.');
};

/**
 * Reformats an amount while it is being typed: groups the integer part, keeps at most two
 * decimals, and keeps a trailing decimal marker so the user can go on typing the decimals.
 * @param {string} rawValue - Raw text as typed.
 * @returns {{formattedValue: string, numericValue: number|null}}
 */
export const formatAmountTyping = (rawValue) => {
  const { decimal, locale } = getMoneySeparators();
  let cleanValue = String(rawValue).replace(/[^0-9.,]/g, '');
  if (decimal === '.') cleanValue = cleanValue.replace(/,/g, '');
  if (cleanValue === '') return { formattedValue: '', numericValue: null };

  const parts = cleanValue.replace(',', '.').split('.');
  let integerPart = parts[0].replace(/\D/g, '');
  const decimalPart = parts[1] !== undefined ? parts[1].slice(0, 2) : null;

  if (integerPart) {
    integerPart = new Intl.NumberFormat(locale).format(integerPart).replace(/\u00A0/g, ' ');
  }

  let formattedValue = integerPart;
  if (decimalPart !== null) {
    formattedValue += `${decimal}${decimalPart}`;
  } else if (hasDecimalMarker(cleanValue)) {
    formattedValue += decimal;
  }

  return { formattedValue, numericValue: parseAmount(formattedValue) };
};

/**
 * Where the caret belongs after formatAmountTyping rewrote the text: after the same number of
 * digits as before, and past the decimal marker if it was past one before.
 * @param {string} formattedValue - The reformatted text.
 * @param {number} digitsBeforeCursor - Digits before the caret in the raw text.
 * @param {boolean} hadDecimalBeforeCursor - Whether the raw text before the caret held a marker.
 * @returns {number}
 */
export const caretOffsetAfterFormat = (formattedValue, digitsBeforeCursor, hadDecimalBeforeCursor) => {
  const { decimal } = getMoneySeparators();
  let newOffset = 0;
  let digitsCount = 0;
  let decimalFound = false;

  for (let i = 0; i < formattedValue.length; i += 1) {
    if (/\d/.test(formattedValue[i])) {
      digitsCount += 1;
    } else if (formattedValue[i] === decimal) {
      decimalFound = true;
    }

    if (digitsCount === digitsBeforeCursor) {
      newOffset = i + 1;
      if (hadDecimalBeforeCursor && decimalFound) break;
      if (!hadDecimalBeforeCursor) break;
    }
  }

  if (hadDecimalBeforeCursor && !decimalFound && formattedValue.includes(decimal)) {
    newOffset = formattedValue.indexOf(decimal) + 1;
  }

  return Math.min(newOffset, formattedValue.length);
};

/**
 * Handles 'on-the-fly' formatting for standard input elements.
 * @param {Event|string} eventOrValue - The input event or raw string value.
 * @param {Function} updateCallback - Callback to update the value in the component.
 *                                   Receives (formattedValue, numericValue).
 */
export const handleAmountInput = (eventOrValue, updateCallback) => {
  const isEvent = typeof eventOrValue !== 'string' && eventOrValue?.target;
  const value = isEvent ? eventOrValue.target.value : eventOrValue;
  const { formattedValue, numericValue } = formatAmountTyping(value);
  updateCallback(formattedValue, numericValue);
};

/**
 * Handles 'on-the-fly' formatting for contenteditable elements, maintaining cursor position.
 * @param {Event} event - The input event.
 * @param {Function} updateCallback - Optional callback for additional logic.
 */
export const handleContentEditableInput = (event, updateCallback) => {
  const input = event.target;
  const originalValue = input.innerText;
  const selection = window.getSelection();
  const range = selection.getRangeAt(0);
  const cursorOffset = range.startOffset;

  // Get text before cursor to track position
  const textBeforeCursor = originalValue.substring(0, cursorOffset);
  const digitsBeforeCursor = textBeforeCursor.replace(/\D/g, '').length;
  const hadDecimalBeforeCursor = hasDecimalMarker(textBeforeCursor);

  const { formattedValue } = formatAmountTyping(originalValue);

  if (input.innerText !== formattedValue) {
    input.innerText = formattedValue;

    // Restore cursor position
    const newRange = document.createRange();
    const newSelection = window.getSelection();
    const newOffset = caretOffsetAfterFormat(formattedValue, digitsBeforeCursor, hadDecimalBeforeCursor);
    newRange.setStart(input.childNodes[0] || input, newOffset);
    newRange.collapse(true);
    newSelection.removeAllRanges();
    newSelection.addRange(newRange);
  }

  if (updateCallback) updateCallback(formattedValue);
};
